import { Link } from 'react-router-dom';
import type { Video } from '@/mocks/videos';

interface AppleShowcaseProps {
  videos: Video[];
}

export default function AppleShowcase({ videos }: AppleShowcaseProps) {
  if (videos.length === 0) return null;
  const [featured, ...rest] = videos;
  const list = rest.slice(0, 4);

  return (
    <section className="bg-background-100 px-4 py-12 md:px-8 md:py-16" aria-label="苹果产品视频精选">
      <div className="mb-8 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            <i className="ri-apple-fill" aria-hidden="true" />
            苹果产品视频精选
          </div>
          <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            读懂每一款新品
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            新品发布、权威评测与实用教程，帮你快速了解产品真实表现。
          </p>
        </div>
        <Link
          to="/category/apple"
          className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
        >
          进入苹果视频频道
          <i className="ri-arrow-right-line" aria-hidden="true" />
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Link
          to={`/detail/${featured.id}`}
          className="group relative flex min-h-[280px] flex-col justify-end overflow-hidden rounded-lg lg:min-h-[420px]"
        >
          <img
            src={featured.cover}
            alt={`${featured.title} 封面`}
            title={`${featured.title} - 苹果视频`}
            className="absolute inset-0 h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent" />
          <div className="relative p-5 md:p-7">
            <span className="mb-3 inline-block rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
              {featured.appleCategory ?? '精选'}
            </span>
            <h3 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              {featured.title}
            </h3>
            <p className="mt-2 line-clamp-2 text-sm text-background-200">
              {featured.shortDesc}
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-background-50">
              <i className="ri-play-circle-line text-lg" aria-hidden="true" />
              观看视频
            </span>
          </div>
        </Link>

        <div className="flex flex-col gap-3">
          {list.map((video) => (
            <Link
              key={video.id}
              to={`/detail/${video.id}`}
              className="group flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-50 p-3 transition-all duration-300 hover:border-primary-200 md:p-4"
            >
              <div className="relative h-20 w-32 flex-shrink-0 overflow-hidden rounded-md bg-background-200">
                <img
                  src={video.cover}
                  alt={`${video.title} 封面`}
                  title={`${video.title} - 苹果视频`}
                  className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute bottom-1 right-1 rounded bg-foreground-950/70 px-1.5 py-0.5 text-[10px] text-background-50">
                  {video.duration}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">
                    {video.appleCategory ?? '苹果'}
                  </span>
                  <span className="text-[11px] text-foreground-400">
                    {video.uploadDate}
                  </span>
                </div>
                <h4 className="line-clamp-1 text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-base">
                  {video.title}
                </h4>
                <p className="mt-1 line-clamp-1 text-xs text-foreground-600">
                  {video.shortDesc}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}